import React from 'react';
import { CalendarDaysIcon, MapPinIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Eyebrow, Section } from '../ui/Section';
import { ButtonAnchor, ButtonExternal } from '../ui/Button';
import { PhotoFrame } from '../ui/PhotoFrame';
import { DoodleArrow, DoodleSquiggle, DoodleStar, DoodleUnderline } from '../brand/Doodles';
import { externalLinks, meeting } from '../../data/site';
import { heroPhotos } from '../../data/photos';
export function Hero() {
  const reduceMotion = useReducedMotion();
  const float = reduceMotion ? {} : {
    animate: {
      y: [0, -8, 0]
    },
    transition: {
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  };
  return <Section className="bcm-paper overflow-hidden pb-16 pt-12 sm:pb-20 sm:pt-16" labelledBy="hero-heading" data-id="element-156" data-mp="3rxyrqp4h3xlw">
      <Container className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14" data-id="element-157" data-mp="1v3vbhdi2lxcm">
        <div data-id="element-158" data-mp="1q6los5m2fzsh">
          <Eyebrow data-id="element-159" data-mp="1q63jkjhfeb8f">Baptist Collegiate Ministries at UCF</Eyebrow>

          <h1 id="hero-heading" className="mt-4 font-display text-[2.15rem] font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-[3.85rem]" data-id="element-160" data-mp="1mw2sr7qlgc42">
            Find your people.
            <span className="relative mt-1 block w-fit" data-id="element-161" data-mp="1czzh9b9ur14g">
              Grow in faith.
              <DoodleUnderline className="absolute -bottom-2 left-0 h-3 w-full sm:-bottom-3 sm:h-4" data-id="element-162" data-mp="1hxrl93nrudp0" />
            </span>
          </h1>

          <p className="mt-7 max-w-xl font-sans text-[17px] leading-relaxed text-ink sm:text-lg" data-id="element-163" data-mp="25p6jak8im8ug">
            UCF BCM is a Christian community where college students can explore faith, build
            meaningful friendships, and learn what it means to follow Jesus. You do not have to be
            Baptist—or even Christian—to walk in. Students from every background are welcome.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-164" data-mp="2ojvb0579amro">
            <ButtonAnchor href="#first-thursday" data-id="element-165" data-mp="2tdpsd1ze2pnl">Plan Your First Thursday</ButtonAnchor>
            <ButtonExternal href={externalLinks.getConnectedForm} variant="secondary" data-id="element-166" data-mp="337vcc1be7xmj">
              Get Connected
            </ButtonExternal>
          </div>

          <div className="mt-9 max-w-lg rounded-[20px] border-2 border-brand-navy bg-white p-5" data-id="element-167" data-mp="2u773dl0uwngi">
            <p className="flex items-start gap-2.5 font-meta text-sm font-semibold text-brand-navy" data-id="element-168" data-mp="2p95f8lokpxy6">
              <CalendarDaysIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" data-id="element-169" data-mp="2k9sqtwi20ith" />
              {meeting.summary}
            </p>
            <p className="mt-2.5 flex items-start gap-2.5 font-meta text-sm text-ink" data-id="element-170" data-mp="2fh9a4qo2l7kg">
              <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-171" data-mp="2akiziiv1b46f" />
              {meeting.location}
            </p>
            <p className="mt-3 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-172" data-mp="21j1jf0hmn2hq">
              {meeting.clarification}
            </p>
          </div>
        </div>

        <div className="relative" data-id="element-173" data-mp="1wkqb4u6zd01m">
          <DoodleStar className="absolute -left-3 -top-4 z-10 h-9 w-9 sm:-left-6 sm:h-12 sm:w-12" data-id="element-174" data-mp="c1w6xc0jg5ho" />
          <DoodleArrow className="absolute -bottom-6 -left-2 z-10 hidden h-16 w-16 lg:block" color="var(--bcm-pink)" data-id="element-175" data-mp="h10ksfyeqrec" />
          <DoodleSquiggle className="absolute -right-2 -top-7 z-10 h-8 w-28 sm:-right-6 sm:h-10 sm:w-36" data-id="element-176" data-mp="m0likn1uctph" />

          <div className="grid grid-cols-5 gap-3 sm:gap-4" data-id="element-177" data-mp="q0vxwqbiwwh0">
            <motion.div className="col-span-5 sm:col-span-3" {...float} data-id="element-178" data-mp="3phlkt792aa5b">
              <PhotoFrame photo={heroPhotos[0]} ratio="aspect-[4/5]" rotate="-rotate-1" className="h-full" data-id="element-179" data-mp="3ufwvtlb3eqp4" />
            </motion.div>
            <div className="col-span-5 flex flex-row gap-3 sm:col-span-2 sm:flex-col sm:gap-4" data-id="element-180" data-mp="73cm5exokzqw">
              <PhotoFrame photo={heroPhotos[1]} ratio="aspect-square" rotate="rotate-2" className="min-w-0 flex-1" data-id="element-181" data-mp="36qkpct75gwqi" />
              <PhotoFrame photo={heroPhotos[2]} ratio="aspect-square" rotate="-rotate-2" className="min-w-0 flex-1" data-id="element-182" data-mp="2rn1cn2nugfpt" />
            </div>
          </div>
          <p className="mt-5 font-meta text-[12.5px] text-ink" data-id="element-183" data-mp="31e5qu05p3wjl">
            Photos from the BCM archive: Pickleball Night &amp; Tabling, Summer ’26.
          </p>
        </div>
      </Container>
    </Section>;
}